import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { devLog } from "@/lib/log";
import Disclaimer from "@/components/Disclaimer";
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import { formatMoney } from "@/shared";
import BasOneTap from "@/components/BasOneTap";
import TparDraftCard from "@/components/tax/TparDraftCard";
import PsiCheckCard from "@/components/tax/PsiCheckCard";
import DeductionCheckerCard from "@/components/tax/DeductionCheckerCard";
import HomeOfficeVehicleCard from "@/components/tax/HomeOfficeVehicleCard";
import QuarterlyHealthCard from "@/components/tax/QuarterlyHealthCard";
import AtoRulingsCard from "@/components/tax/AtoRulingsCard";
import AtoUpdatesCard from "@/components/tax/AtoUpdatesCard";
import PersonalTaxQuickView from "@/components/PersonalTaxQuickView";
import RulesetStamp from "@/components/tax/RulesetStamp";
import FlagsPreflightCard from "@/components/tax/FlagsPreflightCard";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";
import FySummaryCard from "@/components/tax/FySummaryCard";
import AccountantReviewCard from "@/components/tax/AccountantReviewCard";
import AccountantTipsCard from "@/components/tax/AccountantTipsCard";
import TaxFeedbackNudge from "@/components/tax/TaxFeedbackNudge";
import { Sparkles, PieChart, FileDown, Loader2, Share2, MessageCircle, Mail, Calculator, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useApp } from "@/contexts/AppContext";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import { Link } from "react-router-dom";

const LABELS = {
  materials: "Materials",
  equipment: "Tools & Equipment",
  vehicle: "Fuel & Vehicle",
  motor_vehicle: "Vehicle (estimate)",
  wfh: "Home office (estimate)",
  subcontractor: "Subcontractor",
  site: "Site Costs",
  safety: "PPE & Safety",
  admin: "Admin & Office",
  travel: "Meals & Travel",
  uncategorised: "Uncategorised",
};

export default function TaxPage() {
  const { profile } = useApp();
  const { accountantName, accountantEmail, shareVia: shareToAccountant, prefetchPdf } = useShareToAccountant();
  const bizName = profile?.business_name || "";
  const abn = profile?.abn || "";
  const [bas, setBas] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);
  // Business | Personal segment control. Business is the default surface
  // (BAS + Accountant pack + TPAR + FY summary + flags). Personal holds
  // the rentals / investments / deductions preview and its own PDF —
  // pulled out of the assistant so the tradie can reach it directly.
  // Deep-linkable via `?tab=personal` so assistant chips + push
  // notifications can land straight on the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "personal" ? "personal" : "business";
  const [taxMode, setTaxMode] = useState(initialTab);
  useEffect(() => {
    const wanted = searchParams.get("tab") === "personal" ? "personal" : "business";
    if (wanted !== taxMode) setTaxMode(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = useCallback((next) => {
    setTaxMode(next);
    const p = new URLSearchParams(searchParams);
    if (next === "personal") p.set("tab", "personal");
    else p.delete("tab");
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // Tax AI relies on the global --fab-bottom (index.css :root) — 6.5rem
  // sits the FAB ~30px above the bottom nav, and --content-pad-bottom
  // gives the last card ~24px clearance. Local override removed on this
  // pass so Tax AI matches Capture / Queue / Ledger / Jobs spacing.
  const [shareBusy, setShareBusy] = useState(false);
  // Full-screen in-app PDF preview sheet. Every Download / Share tap on
  // this page opens it FIRST — the tradie sees the pages before any
  // OS share sheet is invoked. See PdfPreviewSheet for the rationale.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);
  // "More tools" collapsible — CLOSED by default per spec. Houses every
  // secondary card so the first screen shows only BAS + flags + share +
  // TPAR + FY + pack download + ruleset stamp.
  const [moreOpen, setMoreOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([api.get("/tax/bas-draft"), api.get("/summary")]);
      setBas(b.data);
      setSummary(s.data);
    } catch (e) {
      devLog.warn("tax load failed", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Warm the Accountant Pack PDF cache on mount so the Share buttons can
  // fire `navigator.share({files})` synchronously and the iOS native
  // share sheet appears immediately. Prefetch BOTH URLs the page uses —
  // Download button hits `/export/pdf` (no query), Share buttons hit
  // `/export/pdf?with_evidence=1`.
  useEffect(() => {
    prefetchPdf("/export/pdf", "buildintel-accountant-pack.pdf");
    prefetchPdf("/export/pdf?with_evidence=1", "buildintel-accountant-pack.pdf");
  }, [prefetchPdf]);

  /**
   * Second copy of the FAB-lift effect used to live here — removed on
   * this pass. Global --fab-bottom handles positioning; a per-page
   * override would break the uniform 16px-above-tab-bar rule.
   */
  useEffect(() => {
    // Intentionally empty — kept as a placeholder so future work can
    // hook lifecycle side-effects without re-ordering the file.
    return undefined;
  }, []);

  // Kept for backward compatibility / desktop escape hatch — but the
  // Download button now routes through the preview sheet below so the
  // tradie sees the pages before any share/save flow.
  const grabPdf = async () => {
    setDlBusy(true);
    try {
      await downloadFile("/api/export/pdf", "buildintel-accountant-pack.pdf");
      toast.success("Accountant Pack downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDlBusy(false);
    }
  };

  /**
   * Build a rich text summary of the Accountant Pack for share channels.
   * WhatsApp / Email deep-links can't carry PDF attachments, so this text
   * primes the accountant with the key numbers while the PDF is sent
   * separately (native share attaches it; otherwise it's already on the
   * tradie's device from the Download button).
   */
  const buildPackSummary = useCallback(() => {
    if (!bas) return "";
    const greeting = accountantName ? `Hi ${accountantName},\n\n` : "";
    // Lead with the tradie's business, not BuildIntel. Accountants recognise
    // their client by business name — the AI credit belongs in the sign-off.
    const bizLine = bizName ? `${bizName}${abn ? ` · ABN ${abn}` : ""}\n` : "";
    const cats = bas.deductions_by_category || {};
    const catLines = Object.entries(cats)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 6)
      .map(([k, v]) => `• ${LABELS[k] || k}: $${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      .join("\n");
    const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      `${greeting}${bizLine}Accountant Pack\n` +
      `\n` +
      `Total purchases: ${fmt(bas.total_purchases)}\n` +
      `GST paid on purchases: ${fmt(bas.gst_paid_on_purchases)}\n` +
      `\n` +
      (catLines ? `Deductions by category:\n${catLines}\n\n` : "") +
      `Full PDF pack attached (prepared with BuildIntel AI). Please review before lodging.`
    );
  }, [bas, accountantName, bizName, abn]);

  // Every share entry point now routes through the in-app preview sheet
  // — Download, WhatsApp, Email and Share all open it. From inside the
  // preview the tradie can tap Share to invoke the OS share sheet
  // (which surfaces WhatsApp / Mail / Files / Save). We NEVER call
  // `navigator.share` on the first tap on this page.
  const openPackPreview = useCallback(() => {
    if (!profile) {
      toast.error("Business profile still loading — try again in a moment");
      return;
    }
    const text = buildPackSummary();
    const subject = bizName
      ? `${bizName} — Accountant Pack`
      : "Accountant Pack — BuildIntel AI";
    setPreviewCfg({
      pdfUrl: "/export/pdf?with_evidence=1",
      filename: `${(bizName || "buildintel").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-accountant-pack.pdf`,
      title: "Accountant Pack",
      description: bizName ? `${bizName} · Review before you Share` : "Review before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  }, [buildPackSummary, bizName, profile]);
  // Alias retained for the existing WhatsApp/Email/Share buttons that
  // previously called sharePack directly. All three now open the same
  // in-app preview sheet.
  // eslint-disable-next-line no-unused-vars
  const sharePack = useCallback(async (_kind) => {
    openPackPreview();
  }, [openPackPreview]);

  if (!bas || !summary) return (
    <div className="bi-elevate p-10 text-center text-white/60 inline-flex items-center gap-2 justify-center w-full">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading Tax AI…
    </div>
  );
  const cats = bas.deductions_by_category || {};
  const maxVal = Math.max(1, ...Object.values(cats));

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Sparkles}
        title="Tax AI"
        subtitle="Deductions · BAS draft · PAYG · TPAR · PSI"
        testid="tax-header"
      />

      {/*
        Business | Personal segment control — same chip family as the
        Ledger's All/Pending toggle. Business is the default; Personal
        houses the rentals/investments/deductions preview + its own PDF.
        Compact, single row, keyboard-accessible.
      */}
      <div
        role="tablist"
        aria-label="Tax scope"
        data-testid="tax-scope-tabs"
        className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.06]"
      >
        {[
          { id: "business", label: "Business" },
          { id: "personal", label: "Personal" },
        ].map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={taxMode === t.id}
            data-testid={`tax-scope-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`h-10 rounded-lg text-[12px] font-heading font-bold uppercase tracking-wider transition ${
              taxMode === t.id
                ? "bg-[#FF9500] text-white shadow-sm"
                : "text-white/60 hover:text-white active:scale-[0.98]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {taxMode === "personal" ? (
        // ---- PERSONAL SURFACE -----------------------------------------
        // Pulled out of the assistant path — direct access for tradies
        // who want to review rentals / investments / personal deductions
        // without conversational drilling. Same disclaimer + ruleset
        // stamp footer used on the Business tab.
        <div className="space-y-2.5" data-testid="tax-personal">
          <AtoDisclaimer context="tax_summary" compact testId="tax-disclaimer-personal-top" />
          <PersonalTaxQuickView />
          <DeductionCheckerCard />
          <HomeOfficeVehicleCard />
          <div className="text-center pt-2" data-testid="tax-ruleset-personal">
            <RulesetStamp />
          </div>
        </div>
      ) : (
      // ---- BUSINESS SURFACE (default) ----------------------------------
      // Per product spec the FIRST screen shows only: BAS One-Tap → flags
      // card (Accountant Review, same source as BAS issues) → single share
      // cluster → TPAR → FY summary + Pack download → helper + ruleset
      // stamp. Everything else lives under the closed "More tools" toggle.
      <>
      <BasOneTap />

      {/* Single flags card — Accountant Review uses the same smart-flag
          engine as BAS issues, so counts always agree. When zero it goes
          quiet (no warning icon); when > 0 it lists the items. */}
      <AccountantReviewCard fy={2026} />

      {/* Single share cluster — WhatsApp / Email / Share (all route
          through the in-app PDF preview sheet). */}
      <div className="bi-surface p-4" data-testid="pack-share-row">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading mb-2">
          <Share2 className="w-3 h-3" /> Send pack to {accountantName ? accountantName : "accountant"}
          {accountantEmail && (
            <span className="text-white/30 normal-case tracking-normal truncate">· {accountantEmail}</span>
          )}
          {!profile && (
            <span className="ml-auto text-white/30 normal-case tracking-normal inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> loading profile…
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="pack-share-whatsapp"
            onClick={() => sharePack("whatsapp")}
            disabled={shareBusy || !profile}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition disabled:opacity-60"
          >
            <MessageCircle className="w-4 h-4 text-[#25D366]" />
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            data-testid="pack-share-email"
            onClick={() => sharePack("email")}
            disabled={shareBusy || !profile}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition disabled:opacity-60"
          >
            <Mail className="w-4 h-4 text-[#FF9500]" />
            <span>Email</span>
          </button>
          <button
            type="button"
            data-testid="pack-share-native"
            onClick={() => sharePack("native")}
            disabled={shareBusy || !profile}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition disabled:opacity-60"
          >
            {shareBusy ? <Loader2 className="w-4 h-4 animate-spin text-[#FF9500]" /> : <Share2 className="w-4 h-4 text-[#FF9500]" />}
            <span>Share</span>
          </button>
        </div>
      </div>

      <TparDraftCard />

      {/* FY summary card owns its own Download FY PDF button. Accountant
          Pack download sits directly under it as the second CTA. */}
      <FySummaryCard />

      <button
        data-testid="tax-download-pdf"
        onClick={openPackPreview}
        disabled={dlBusy || !profile}
        className="w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
      >
        {dlBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileDown className="w-5 h-5" />}
        {dlBusy ? "Building your PDF…" : "Download Accountant Pack (PDF)"}
      </button>

      {/* ─── More tools (closed by default) ───────────────────────────
          Every secondary card lives here so the first screen stays
          BAS-primary. Order preserved from the previous layout. */}
      <div className="bi-surface rounded-2xl overflow-hidden" data-testid="tax-more-tools">
        <button
          type="button"
          data-testid="tax-more-tools-toggle"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 h-14 text-left hover:bg-white/[0.02] transition"
        >
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-heading font-bold">
            More tools
          </span>
          <ChevronRight
            className={`w-4 h-4 text-white/40 transition-transform ${moreOpen ? "rotate-90" : ""}`}
          />
        </button>
        {moreOpen && (
          <div className="p-4 pt-0 space-y-4" data-testid="tax-more-tools-body">
            <QuarterlyHealthCard />
            <DeductionCheckerCard />
            <Link
              to="/vehicle-wfh"
              data-testid="tax-vehicle-wfh-link"
              className="bi-surface-2 p-3 flex items-center gap-3 hover:bg-white/[0.03] transition rounded-xl"
            >
              <div className="w-9 h-9 rounded-lg bg-[#FF9500]/15 border border-[#FF9500]/25 flex items-center justify-center shrink-0">
                <Calculator className="w-4 h-4 text-[#FF9500]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-[13px] text-white">
                  Deduction calculators (vehicle &amp; WFH)
                </div>
                <div className="text-[11px] text-white/50 truncate">
                  Cents-per-km + fixed-rate estimates. Confirm before they hit your ledger.
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />
            </Link>
            <HomeOfficeVehicleCard />
            <PsiCheckCard />
            <AtoRulingsCard />
            <AtoUpdatesCard />
            <AccountantTipsCard />
          </div>
        )}
      </div>

      {/* Helper line + ruleset stamp — one sentence, at the very bottom. */}
      <p className="text-[13px] text-white/50 text-center pt-2">
        Helper tool only — confirm figures with your accountant before lodgement.
      </p>
      <div className="text-center" data-testid="tax-ruleset-footer">
        <RulesetStamp />
      </div>

      <div className="sr-only" data-testid="pack-share-anchor" />
      {/* Legacy plain Disclaimer preserved as sr-only fallback for older tests. */}
      <div className="sr-only">
        <Disclaimer text="Helper tool only — not a registered tax agent. Confirm figures with your accountant before lodgement." />
      </div>
      </>
      )}

      <PdfPreviewSheet
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
        pdfUrl={previewCfg?.pdfUrl}
        filename={previewCfg?.filename}
        title={previewCfg?.title}
        description={previewCfg?.description}
        shareText={previewCfg?.shareText}
        shareSubject={previewCfg?.shareSubject}
        testid="tax-pack-preview"
      />
    </div>
  );
}
